import {Box, Heading, HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';

import {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import {TelephonyCall} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {DOT_CHARACTER} from 'shared/web';

import {useCallCustomerName} from '../../../hooks/useCallCustomerName';
import {getCallPhoneNumberLabel} from '../../../utils/getCallPhoneNumberLabel';

interface TelephonyCallPanelCallerProps {
  call: TelephonyCall;
  status: string;
  dotColor: ThemeColorPath;
  /** Call controls and the way to the customer. */
  actions?: ReactNode;
  /** Extra state under the status, e.g. the press-1 steps of a mobile call. */
  children?: ReactNode;
}

const AVATAR_SIZE = 10;
const DOT_SIZE = 2;

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

/** The top of the call panel: who the viewer is talking to, first and largest. */
export function TelephonyCallPanelCaller(props: TelephonyCallPanelCallerProps) {
  const name = useCallCustomerName(props.call);
  const phoneNumber = isNotNil(props.call.customerPhoneNumber)
    ? formatPhoneNumber(props.call.customerPhoneNumber)
    : getCallPhoneNumberLabel(props.call);
  const isUnknown = isNil(name);

  return (
    <Box padding={4}>
      <VStack spacing={3}>
        <HStack spacing={3} align="center">
          <Box
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            flexShrink={0}
            borderRadius="circular"
            backgroundColor={isUnknown ? 'palettes.neutral.30.100' : 'palettes.blue.20.100'}
          >
            <HStack height="100%" align="center" justify="center">
              <Text alternative color={isUnknown ? 'secondary' : 'link'}>
                {isUnknown ? '?' : getInitials(name)}
              </Text>
            </HStack>
          </Box>
          <VStack spacing={1} minWidth={0}>
            <Heading size={3} overflowWrap="anywhere">
              {name ?? phoneNumber}
            </Heading>
            <Text size="small" color="secondary">
              {isUnknown ? i18n.t('page.telephony.indicator.unknownCaller') : phoneNumber}
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={2} align="center">
          <Box
            width={DOT_SIZE}
            height={DOT_SIZE}
            flexShrink={0}
            borderRadius="circular"
            backgroundColor={props.dotColor}
          />
          <Text size="small">{props.status}</Text>
          <Text size="small" color="secondary">
            {`${DOT_CHARACTER} ${props.call.line.name}`}
          </Text>
        </HStack>
        {props.children}
        <Show when={isNotNil(props.actions)}>
          <HStack spacing={2} align="center" wrap>
            {props.actions}
          </HStack>
        </Show>
      </VStack>
    </Box>
  );
}
